import { useTranslation } from 'react-i18next';
import { LADDER_LABEL_KEY } from '@d-orca/shared/client/activities';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { WarningTriangleIcon } from '@utils/icons';
import rowStyles from '../../SchedulerPage/components/RowLabels/RowLabels.module.css';
import { TaskRailCells } from '../../SchedulerPage/components/RowLabels/RailCells';
import { DayShading } from './DayShading';
import { PromiseBar } from './PromiseBar';
import type { IPlacedRow, IPlanWindow } from './workPlanModel';
import styles from './workPlanRow.module.css';

export interface IWorkPlanRowProps {
  row: IPlacedRow;
  window: IPlanWindow;
  today: string;
  /** Off when the rows are grouped by that same fact — the band says it. */
  showProfession: boolean;
  showAssignee: boolean;
  selected: boolean;
  onOpen: (taskId: string) => void;
}

/**
 * One promise, as the Gantt draws a task: the rail row (name, then the
 * Scheduler's own profession · progress · status cells) and its day track —
 * the promise's bar and, when it is overdue, its overrun to today.
 * The name button is the row's one control; a click anywhere on the row opens
 * the drawer too, and the track stays out of the accessibility tree.
 */
export function WorkPlanRow({ row, window, today, showProfession, showAssignee, selected, onOpen }: IWorkPlanRowProps) {
  const { t } = useTranslation();
  const { task, rung } = row;
  const open = () => onOpen(task.id);

  return (
    <div className={styles.row} data-selected={selected || undefined}>
      <div className={styles.rail} onClick={open}>
        <button type="button" className={styles.name} aria-label={`${task.name} · ${t(LADDER_LABEL_KEY[rung])}`}>
          <span className={rowStyles.taskName}>{task.name}</span>
          {task.dangerousActivity && (
            <Tooltip content={t('scheduler.commit.dangerous')}>
              <span className={styles.danger} role="img" aria-label={t('scheduler.commit.dangerous')}>
                <WarningTriangleIcon size={12} />
              </span>
            </Tooltip>
          )}
          {showAssignee && task.assignee && <span className={styles.assignee}>{task.assignee}</span>}
        </button>
        <TaskRailCells task={task} withProfession={showProfession} />
      </div>
      <div className={styles.time} aria-hidden onClick={open}>
        <DayShading window={window} />
        <PromiseBar row={row} today={today} />
      </div>
    </div>
  );
}
